import React from 'react';
import { View, TouchableOpacity, StyleSheet, Platform } from 'react-native';
import Text from '../common/Text';
import Button from '../common/Button';
import { ProductDef } from '../../data/landingData';

type ThemeColors = {
  card: string;
  border: string;
  cardText: string;
  text: string;
  secondary: string;
};

type Props = {
  product: ProductDef;
  _index: number;
  isHovered: boolean;
  isMobile: boolean;
  colors: ThemeColors;
  onPress: () => void;
  onTry: () => void;
  onHoverIn?: () => void;
  onHoverOut?: () => void;
};

const FeatureCard: React.FC<Props> = ({
  product,
  _index,
  isHovered,
  isMobile,
  colors,
  onPress,
  onTry,
  onHoverIn,
  onHoverOut,
}) => {
  return (
    <TouchableOpacity
      style={[
        styles.card,
        isMobile && styles.cardMobile,
        {
          backgroundColor: colors.card,
          borderColor: colors.border,
          ...(Platform.OS === 'web'
            ? { boxShadow: `0px 4px 12px 0px ${colors.cardText}1A` }
            : { shadowColor: colors.cardText }),
        },
        isHovered && !isMobile && styles.cardHovered,
      ]}
      onPress={onPress}
      activeOpacity={0.8}
      {...(Platform.OS === 'web' && {
        onMouseEnter: onHoverIn,
        onMouseLeave: onHoverOut,
      })}
    >
      {/* Colored accent bar */}
      <View style={[styles.accentBar, { backgroundColor: product.color }]} />

      {/* Card content */}
      <View style={[styles.content, isMobile && styles.contentMobile]}>
        {/* Body: tag + title + subtitle + description */}
        <View style={[styles.body, isMobile && styles.bodyMobile]}>
          {product.tag && (
            <View
              style={[
                styles.tag,
                isMobile && styles.tagMobile,
                { backgroundColor: product.color + '18' },
              ]}
            >
              <Text style={[styles.tagText, { color: product.color }]}>
                {product.tag}
              </Text>
            </View>
          )}

          <Text
            style={[
              styles.title,
              isMobile && styles.titleMobile,
              { color: colors.text },
              isHovered && !isMobile && styles.titleHovered,
            ]}
          >
            {product.title}
          </Text>

          <Text
            style={[
              styles.subtitle,
              isMobile && styles.subtitleMobile,
              { color: product.color },
            ]}
          >
            {product.subtitle}
          </Text>

          <Text
            style={[
              styles.description,
              isMobile && styles.descriptionMobile,
              { color: colors.secondary },
            ]}
          >
            {product.description}
          </Text>
        </View>

        {/* CTA button */}
        <Button
          title={`${product.cta ?? 'Try It Free'} →`}
          onPress={onTry}
          variant="primary"
          style={[
            styles.button,
            isMobile && styles.buttonMobile,
            { backgroundColor: product.color, borderColor: product.color },
            isHovered && !isMobile && styles.buttonHovered,
          ]}
          textStyle={[styles.buttonText, { color: '#fff' }]}
          trackClick={true}
          trackingLocation="landing-page-features-section"
          trackingData={{
            button_name: product.title,
            feature_name: product.title,
            feature_id: product.id,
            button_type: 'feature_try',
          }}
        />
      </View>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  card: {
    // Desktop: row+wrap grid item — sized by content, constrained by min/max
    flexGrow: 0,
    flexShrink: 1,
    flexBasis: 'auto',
    minWidth: 300,
    maxWidth: 520,
    borderRadius: 20,
    borderWidth: 1,
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.08,
    shadowRadius: 16,
    elevation: 5,
    flexDirection: 'column',
    overflow: 'hidden',
  },
  cardMobile: {
    // Mobile: full-width column stack — NEVER use flex:0 (sets flexBasis:0 = collapse)
    flexGrow: 0,
    flexShrink: 0,
    flexBasis: 'auto',
    minWidth: 0,
    maxWidth: 99999,
    width: '100%',
    alignSelf: 'stretch',
  },
  cardHovered: {
    transform: [{ scale: 1.03 }],
    shadowOpacity: 0.2,
    shadowRadius: 24,
    elevation: 8,
    borderWidth: 2,
    borderColor: 'rgba(124, 58, 237, 0.3)',
  },
  accentBar: {
    height: 5,
    width: '100%',
  },
  content: {
    // Desktop: fill remaining card height — use flexGrow not flex:1 (avoids flexBasis:0)
    flexGrow: 1,
    flexShrink: 0,
    flexBasis: 'auto',
    padding: 24,
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  contentMobile: {
    // Mobile: height from content, not stretched — override flexGrow back to 0
    flexGrow: 0,
    flexShrink: 0,
    flexBasis: 'auto',
    paddingVertical: 14,
    paddingHorizontal: 14,
  },
  body: {
    alignItems: 'flex-start',
    width: '100%',
    marginBottom: 20,
  },
  bodyMobile: {
    marginBottom: 12,
  },
  tag: {
    paddingHorizontal: 12,
    paddingVertical: 5,
    borderRadius: 100,
    marginBottom: 14,
    alignSelf: 'flex-start',
  },
  tagMobile: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    marginBottom: 8,
  },
  tagText: {
    fontSize: 12,
    fontWeight: '700',
    letterSpacing: 0.8,
    textTransform: 'uppercase',
  },
  title: {
    fontSize: 24,
    fontWeight: '800',
    marginBottom: 6,
    textAlign: 'left',
    letterSpacing: -0.5,
    lineHeight: 30,
  },
  titleMobile: {
    fontSize: 18,
    lineHeight: 23,
    letterSpacing: -0.2,
    marginBottom: 4,
  },
  titleHovered: {
    color: 'rgba(124, 58, 237, 0.9)',
  },
  subtitle: {
    fontSize: 15,
    fontWeight: '600',
    marginBottom: 10,
    lineHeight: 22,
  },
  subtitleMobile: {
    fontSize: 12,
    marginBottom: 6,
    lineHeight: 17,
  },
  description: {
    fontSize: 15,
    textAlign: 'left',
    lineHeight: 23,
    opacity: 0.85,
  },
  descriptionMobile: {
    fontSize: 13,
    lineHeight: 19,
  },
  button: {
    paddingVertical: 10,
    paddingHorizontal: 24,
    borderRadius: 12,
    borderWidth: 0,
    height: 44,
    alignSelf: 'stretch',
  },
  buttonMobile: {
    minHeight: 44,
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 10,
  },
  buttonHovered: {
    opacity: 0.9,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.25,
    shadowRadius: 8,
    elevation: 6,
  },
  buttonText: {
    fontSize: 15,
    fontWeight: '700',
    letterSpacing: 0.1,
  },
});

export default FeatureCard;
